import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Loader } from '../Loader';
import { SubscriptionPlanSelectorV9 } from '../SubscriptionPlanSelectorV9';
import { ProtectedListV2 } from '../ProtectedListV2';
import { RiskStatusCards } from '../RiskStatusCards';
import { RiskStatusBar } from '../RiskStatusBar';
import { RiskConcernSummary } from '../RiskConcernSummary';
import { OVERALL_SCORE_THRESHOLDS, calculateRiskScore, getRiskLevel } from '../../shared/risk-helpers';
import { PreCheckoutV8 } from '../PreCheckoutV8';
import { CheckoutV3 } from '../CheckoutV3';
import { Icon } from '../Icon';
import { useSubscriptionPlans } from '../../shared/use-subscription-plans';
import { useIntersectionTracking } from '../../shared/use-intersection-tracking';
import { scrollToPlans } from '../../shared/scroll-helpers';
import { createSubscriptionForm } from '../../../lib/use-cases/create-subscription';
import { handleCreateSubscriptionError } from '../../shared/error-helpers';
import { resolveFirstStepUrl } from '../../../lib/shared/upsell-flow';
import { getCurrentPrelander } from '../../../lib/shared/prelander-helpers';
import { getMetadata } from '../../../lib/shared/ad-helpers';
import { logEvent } from '../../../lib/shared/event-logger';
import { EVENT_TYPES } from '../../../lib/shared/event-types';
import { FP_PROMO_MAIN_TYPE } from '../../../lib/shared/promotion-type';
import { FOUR_WEEKS_3999_FOUR_WEEKS_1999_INTRO_PLAN, FOUR_WEEKS_3999_PLAN, FOUR_WEEKS_3999_TWO_WEEKS_1399_TRIAL_PLAN, FOUR_WEEKS_3999_TWO_WEEKS_1948_TRIAL_PLAN, TWELVE_WEEKS_7999_PLAN, TWELVE_WEEKS_7999_TWELVE_WEEKS_3999_INTRO_PLAN } from '../../../lib/shared/subscription-plans';
import breachImageSrc from '../../assets/images/sale/breach-for-sale.png';
import learnImageSrc from '../../assets/images/sale/learn-for-sale.png';
import extensionImageSrc from '../../assets/images/sale/extension-for-sale-new.png';
import styles from './SaleV19.css';

const RISK_ITEMS = [
    {
        id: 1,
        name: 'Online Fraud & Scam Risk',
        iconName: 'link',
        badLabel: 'Unchecked',
        goodLabel: 'Checked'
    },
    {
        id: 2,
        name: 'Private Data Theft Risk',
        iconName: 'archive',
        badLabel: 'Unmonitored',
        goodLabel: '24/7 Alerts'
    },
    {
        id: 3,
        name: 'Identity Theft Risk',
        iconName: 'userSquare',
        badLabel: 'No Alerts',
        goodLabel: 'Live Alerts'
    },
    {
        id: 4,
        name: 'Financial Theft Risk',
        iconName: 'creditCard',
        badLabel: 'Guessing',
        goodLabel: 'Informed'
    }
];
const SERVICES = [
    {
        id: 1,
        title: 'Data Breach Analyzer',
        description: 'Scan known data breaches and the dark web to see if your passwords or email have been exposed online.',
        imageUrl: breachImageSrc
    },
    {
        id: 2,
        title: 'Security Learning',
        description: 'Explore short, easy-to-follow guides built specifically for beginners. Learn how to navigate the modern internet safely.',
        imageUrl: learnImageSrc
    },
    {
        id: 3,
        title: 'Browser Extension',
        description: 'Helps alert you about unsafe websites and phishing traps while you browse the web.',
        imageUrl: extensionImageSrc
    }
];

const plansWithoutPromo = [FOUR_WEEKS_3999_TWO_WEEKS_1948_TRIAL_PLAN, FOUR_WEEKS_3999_PLAN, TWELVE_WEEKS_7999_PLAN];
const plansWithMainPromo = [FOUR_WEEKS_3999_TWO_WEEKS_1399_TRIAL_PLAN, FOUR_WEEKS_3999_FOUR_WEEKS_1999_INTRO_PLAN, TWELVE_WEEKS_7999_TWELVE_WEEKS_3999_INTRO_PLAN];

function SaleV19({
    data,
    initialPrelander,
    expiryTimestamp,
    onExpirePromo = () => {},
    isExpired = true,
    selectSubscription,
    selectedSubscriptionData,
    merchantData,
    setMerchantData
}) {
    const { plans, isLoading } = useSubscriptionPlans([], isExpired);
    const [isPending, setIsPending] = useState(false);
    const firstUpsellUrl = resolveFirstStepUrl(getCurrentPrelander(), merchantData.subscriptionId);
    const riskLevel = getRiskLevel(calculateRiskScore(data.userResponses), OVERALL_SCORE_THRESHOLDS);

    useIntersectionTracking('.fp-block');

    const handleClose = () => {
        selectSubscription({});
        setMerchantData({});
        scrollToPlans();
    };

    useEffect(() => {
        const handleNavigation = e => {
            if (e.navigationType === 'traverse' && window.history.state?.checkoutVisible) {
                window.history.replaceState({ checkoutVisible: false }, '');
                handleClose();
            }
        };

        logEvent(EVENT_TYPES.SALE_PAGE_VISITED, { 'risk_level': riskLevel });

        window.navigation.addEventListener('navigate', handleNavigation);
        document.getElementById('root').scrollIntoView({ block: 'start', behavior: 'smooth' });

        return () => {
            window.navigation.removeEventListener('navigate', handleNavigation);
        };
    }, []);

    const getPlans = () => {
        if (!isExpired && data.promoType === FP_PROMO_MAIN_TYPE) {
            return plansWithMainPromo.map(plan => plans.find(item => item.plan === plan)).filter(Boolean);
        }

        return plansWithoutPromo.map(plan => plans.find(item => item.plan === plan)).filter(Boolean);
    };

    const submitSubscription = async subscriptionData => {
        try {
            setIsPending(true);

            const newMerchantData = await createSubscriptionForm({
                plan: subscriptionData.plan,
                email: data.email,
                metadata: {
                    ...getMetadata(window.location.search, initialPrelander, data.userResponses),
                    digitalRiskLevel: riskLevel
                }
            });

            window.history.pushState({ checkoutVisible: true }, '');
            setMerchantData(newMerchantData);
            logEvent(EVENT_TYPES.SALE_CHECKOUT_INITIATE);
        } catch (e) {
            handleCreateSubscriptionError(e);
        } finally {
            setIsPending(false);
        }
    };

    const tryAgain = async (subscriptionData, subscriptionId) => {
        try {
            setIsPending(true);

            const newMerchantData = await createSubscriptionForm({
                plan: subscriptionData.plan,
                email: data.email,
                id: subscriptionId,
                metadata: {
                    ...getMetadata(window.location.search, initialPrelander, data.userResponses),
                    digitalRiskLevel: riskLevel
                }
            });

            setMerchantData(newMerchantData);
        } catch (e) {
            handleCreateSubscriptionError(e);
        } finally {
            setIsPending(false);
        }
    };

    const nextPreCheckout = subscriptionData => {
        logEvent(EVENT_TYPES.PLAN_SELECTED, { plan: subscriptionData.plan });
        selectSubscription(subscriptionData);
    };

    if (merchantData.merchant) {
        return (
            <div className={styles.fixedModal}>
                <CheckoutV3
                    merchantData={merchantData}
                    subscriptionData={selectedSubscriptionData}
                    promoType={isExpired ? '' : data.promoType}
                    onClose={handleClose}
                    tryAgain={tryAgain}
                    isPending={isPending}
                    successUrl={firstUpsellUrl}
                />
            </div>
        );
    }

    if (selectedSubscriptionData.plan) {
        return (
            <PreCheckoutV8
                onSubmit={() => submitSubscription(selectedSubscriptionData)}
                isPending={isPending}
            />
        );
    }

    const renderPlans = () => {
        const newPlans = getPlans();

        return (
            <>
                {isLoading
                    ? <Loader className={styles.loader} variant='secondary' />
                    : (
                        <SubscriptionPlanSelectorV9
                            onSubmit={nextPreCheckout}
                            defaultPlan={newPlans[1]?.plan}
                            isPending={isPending}
                            plans={newPlans}
                            expiryTimestamp={expiryTimestamp}
                            onExpirePromo={onExpirePromo}
                            isExpired={isExpired}
                            className={styles.blockWithIndent}
                            defaultDiscountValue={50}
                            renderRiskConcernSummary={() => <RiskConcernSummary userResponses={data.userResponses} />}
                        />
                    )
                }
            </>
        );
    };

    return (
        <div className={styles.root}>
            <div className={styles.profileBlock}>
                <div className={styles.profileBlockHeader}>
                    <p>Right Now</p>
                    <Icon name='chevronRightDouble' />
                    <p>With Futureproof</p>
                </div>
                <RiskStatusCards level={riskLevel} />
                <ul className={styles.riskList}>
                    {RISK_ITEMS.map(item => (
                        <li
                            className={styles.riskListItem}
                            key={item.id}
                        >
                            <p className={styles.riskListItemHeader}>
                                <Icon name={item.iconName} />
                                {item.name}
                            </p>
                            <div className={styles.riskListItemWrapper}>
                                <RiskStatusBar label={item.badLabel} tone='danger' />
                                <div className={styles.divider} />
                                <RiskStatusBar label={item.goodLabel} tone='success' />
                            </div>
                            {item.id === 4 && <div id='subscription-start-block' className={styles.subscriptionStartBlock} />}
                        </li>
                    ))}
                </ul>
            </div>
            <p className={styles.goalDisclaimer}>Risk evaluations are educational and based on quiz responses, not a direct device scan.</p>
            <h2 className={styles.secondaryTitle}>
                Your Protection Plan <br /> is Ready!
            </h2>
            <div id={EVENT_TYPES.FIRST_PLAN_VIEWED} className='fp-block' />
            {renderPlans()}
            <h2 className={styles.secondaryTitle}>Highlights of your plan</h2>
            <ProtectedListV2 className={styles.blockWithIndent} />
            <ul className={styles.serviceList}>
                {SERVICES.map(item => (
                    <li
                        className={styles.serviceListItem}
                        key={item.id}
                    >
                        <div className={styles.serviceListItemWrapper}>
                            <p className={styles.serviceTitle}>{item.title}</p>
                            <p>{item.description}</p>
                        </div>
                        <div
                            style={{ backgroundImage: `url(${item.imageUrl})` }}
                            className={styles.serviceListItemBg}
                        />
                    </li>
                ))}
            </ul>
        </div>
    );
}

SaleV19.propTypes = {
    data: PropTypes.object.isRequired,
    expiryTimestamp: PropTypes.any,
    onExpirePromo: PropTypes.func,
    isExpired: PropTypes.bool,
    initialPrelander: PropTypes.string.isRequired
};

export {
    SaleV19
};
